import type { ComputedRef } from 'vue'
import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import type { AuditResult } from '@/content-review/api'
import { PRESENTLEVELS_EVENT_NAMES } from '@/presentlevels/event_names'
import { calculateNormalizedScores } from '@/content-review/utils/calculateNormalizedScores'

export type PresentLevelsCriteriaEventAttrs = {
  criteria_label: string
  is_custom_criteria_set: boolean
}

export function usePresentLevelsReviewAnalytics(
  analytics: AnalyticsTracker,
  criteriaEventAttrs: ComputedRef<PresentLevelsCriteriaEventAttrs>,
  hasResults: ComputedRef<boolean>
) {
  function handleAutoReviewButtonClick(onOpen: () => void) {
    analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.REVIEW.CLICKED_AUTO_REVIEW_BUTTON, {
      has_existing_results: hasResults.value,
      ...criteriaEventAttrs.value,
    })
    onOpen()
  }

  function handleModalReview(onReview: () => void) {
    analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.REVIEW.CLICKED_AUTO_REVIEW_MODAL_REVIEW, criteriaEventAttrs.value)
    onReview()
  }

  function handleCloseReviewResults(onClose: () => void) {
    analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.REVIEW.CLOSED_REVIEW_RESULTS, criteriaEventAttrs.value)
    onClose()
  }

  function handleReviewResultsToggle(result: { section: string; audit_title: string; score: number; max_score: number }, expanded: boolean) {
    if (expanded) {
      analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.REVIEW.CLICKED_REVIEW_RESULTS_ITEM_TOGGLE, {
        section: result.section,
        criteria_title: result.audit_title,
        score: result.score,
        max_score: result.max_score,
        ...criteriaEventAttrs.value,
      })
    }
  }

  function handleReceivedReviewResults(results: AuditResult[]) {
    analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.REVIEW.RECEIVED_REVIEW_RESULTS, {
      present_levels_audit_scores: results.map((r) => ({
        criteria_title: r.audit_title,
        section: r.section,
        score: r.score,
        max_score: r.max_score,
      })),
      average_normalized_scores: calculateNormalizedScores(results),
      ...criteriaEventAttrs.value,
    })
  }

  return {
    handleAutoReviewButtonClick,
    handleModalReview,
    handleCloseReviewResults,
    handleReviewResultsToggle,
    handleReceivedReviewResults,
  }
}
